<template>
  <v-dialog :value="value" max-width="980" persistent scrollable @input="$emit('input', $event)">
    <v-card>
      <v-card-title class="primary white--text py-3">
        <v-icon color="white" small class="mr-2">fas fa-file-excel</v-icon>
        {{ batch ? 'Cargar otro Excel al lote #' + batch.id + (batch.name ? ' · ' + batch.name : '') : 'Importar distribución de guías' }}
        <v-spacer />
        <v-btn icon dark small :disabled="working" @click="close"><v-icon small>fas fa-times</v-icon></v-btn>
      </v-card-title>

      <v-card-text class="pt-5" style="max-height: 75vh">
        <!-- Paso 1: cabecera + archivo -->
        <template v-if="!validation">
          <div class="caption grey--text font-weight-black text-uppercase mb-2">Destinatario</div>
          <v-row dense v-if="!batch">
            <v-col cols="12" md="3">
              <v-text-field
                v-model="otNumber" label="Orden de Trabajo" placeholder="N° OT" outlined dense type="number"
                :append-icon="otFound ? 'fas fa-check-circle' : 'fas fa-search'" :success="otFound"
                :loading="loadingOt" hint="Opcional" persistent-hint
                @input="searchOt"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="rut" :label="$store.getters.getInternational.tax_id_label || 'RUT'" outlined dense
                :disabled="otFound" :loading="loadingCustomer"
                :hint="customer ? '' : 'Requerido si no hay OT'" persistent-hint
                @input="searchCustomer"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field :value="customer ? customer.name : ''" label="Razón social" outlined dense readonly
                :error-messages="rutNotFound ? ['Cliente no registrado'] : []" />
            </v-col>
          </v-row>
          <v-alert v-else dense text type="info" class="mb-2">
            Lote #{{ batch.id }}<span v-if="batch.workorder"> · OT {{ batch.workorder }}</span> · {{ batch.customer_name }}.
            Las pre-guías se actualizan por N° Local; las ya emitidas o en proceso no se modifican.
          </v-alert>

          <div class="caption grey--text font-weight-black text-uppercase mb-2 mt-2">Datos de las guías</div>
          <v-text-field
            v-model="name" label="Nombre del lote (opcional)" outlined dense counter="150" maxlength="150"
            placeholder="Ej: Jumbo Semana Alemana 2026" hint="Si lo deja vacío se usa el nombre del archivo" persistent-hint
            class="mb-2" @input="nameTouched = true"
          />
          <v-row dense>
            <v-col cols="12" md="4">
              <v-select dense outlined hide-details :items="traslados" item-text="text" item-value="value" label="Indicador de traslado" v-model="params.tipo_traslado" />
            </v-col>
            <v-col cols="12" md="4">
              <v-select dense outlined hide-details :items="despachos" item-text="text" item-value="value" label="Tipo de despacho" v-model="params.tipo_despacho" />
            </v-col>
            <v-col cols="6" md="2">
              <v-text-field dense outlined hide-details type="number" min="0" label="Precio unitario" v-model.number="params.precio_unitario" />
            </v-col>
            <v-col cols="6" md="2">
              <v-text-field dense outlined hide-details type="date" label="Fecha guías" v-model="params.fecha" />
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field dense outlined hide-details label="N° Orden de compra" v-model="params.oc_folio" />
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field dense outlined hide-details type="date" label="Fecha OC" v-model="params.oc_fecha" />
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field dense outlined hide-details label="Patente" v-model="params.transporte.patente" />
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field dense outlined hide-details label="RUT transportista" v-model="params.transporte.rut_trans" />
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field dense outlined hide-details label="Chofer" v-model="params.transporte.nombre_chofer" />
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field dense outlined hide-details label="RUT chofer" v-model="params.transporte.rut_chofer" />
            </v-col>
          </v-row>

          <div class="caption grey--text font-weight-black text-uppercase mb-2 mt-5">Archivo de distribución</div>
          <v-file-input
            v-model="file" accept=".xlsx" label="Excel (.xlsx)" outlined dense
            prepend-icon="" prepend-inner-icon="fas fa-file-excel"
            hint="Fila 1 = encabezados: N° Local, Nombre Local, Dirección, (Comuna opcional) y una columna por producto con la cantidad"
            persistent-hint
          />
        </template>

        <!-- Paso 2: resultado de la validación -->
        <template v-else>
          <div class="d-flex flex-wrap align-center mb-3" style="gap: 8px">
            <v-chip label color="primary" dark>{{ validation.locales }} locales</v-chip>
            <v-chip label>{{ validation.products }} productos</v-chip>
            <v-chip label color="success" dark>{{ validation.created }} pre-guías nuevas</v-chip>
            <v-chip v-if="validation.updated" label color="info" dark>{{ validation.updated }} se actualizan</v-chip>
            <span v-if="validation.customer" class="ml-2">Cliente: <b>{{ validation.customer.name }}</b> ({{ validation.customer.rut }})</span>
          </div>
          <v-alert v-if="otherBatches.length" dense text type="warning">
            Esta OT ya tiene lote(s) {{ otherBatches.map(id => '#' + id).join(', ') }}. Si es la misma distribución, use «Cargar otro Excel» dentro de ese lote para no duplicar guías.
          </v-alert>
          <v-alert v-if="validation.errors.length" dense text type="error">
            <div class="font-weight-bold">{{ validation.errors.length }} filas con error no se cargarán:</div>
            <div v-for="(e, i) in validation.errors.slice(0, 15)" :key="'e' + i" class="caption">{{ e }}</div>
            <div v-if="validation.errors.length > 15" class="caption">… y {{ validation.errors.length - 15 }} más</div>
          </v-alert>
          <v-alert v-if="validation.warnings.length" dense text type="warning">
            <div v-for="(w, i) in validation.warnings.slice(0, 15)" :key="'w' + i" class="caption">{{ w }}</div>
            <div v-if="validation.warnings.length > 15" class="caption">… y {{ validation.warnings.length - 15 }} más</div>
          </v-alert>
          <div class="caption font-weight-bold mb-1">Total a despachar por producto</div>
          <div class="mb-3">
            <v-chip v-for="t in totals" :key="t.name" small label class="mr-1 mb-1">{{ t.qty }} × {{ t.name }}</v-chip>
          </div>
          <v-data-table :headers="previewHeaders" :items="validation.preview" item-key="code" dense :items-per-page="10" class="border" />
        </template>
      </v-card-text>

      <v-divider />
      <v-card-actions class="pa-4">
        <v-btn v-if="validation" text :disabled="working" @click="validation = null"><v-icon left small>fas fa-arrow-left</v-icon>Volver</v-btn>
        <v-spacer />
        <v-btn text :disabled="working" @click="close">Cancelar</v-btn>
        <v-btn v-if="!validation" color="primary" :disabled="!canValidate" :loading="working" @click="validate">
          <v-icon left small>fas fa-check</v-icon>Validar
        </v-btn>
        <v-btn v-else color="success" :disabled="!validation.locales" :loading="working" @click="load">
          <v-icon left small>fas fa-upload</v-icon>Cargar {{ validation.locales }} pre-guías
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment'
import notifications from '@/utils/notifications'
import { getWorkOrderforDTE } from '@/api/salesNotes'
import { getCustomerByRUT } from '@/api/customers'
import { validaRUT } from '@/utils/rut_validator'
import { uploadDispatchDistribution } from '@/api/facturacioncl'

function emptyParams() {
  return {
    tipo_traslado: 1, tipo_despacho: 1, precio_unitario: 1, fecha: moment().format('YYYY-MM-DD'),
    oc_folio: '', oc_fecha: null, transporte: {},
  }
}

export default {
  name: 'BulkDispatchImportDialog',
  props: {
    value: { type: Boolean, default: false },
    batch: { type: Object, default: null }, // si viene, se recarga ese lote
    initialOt: { type: [String, Number], default: null },
  },
  data() {
    return {
      otNumber: '',
      otFound: false,
      loadingOt: false,
      otTimer: null,
      rut: '',
      customer: null,
      rutNotFound: false,
      loadingCustomer: false,
      rutTimer: null,
      params: emptyParams(),
      file: null,
      name: '',
      nameTouched: false,
      validation: null,
      working: false,
      traslados: [
        { value: 1, text: '1 - Operación constituye venta' },
        { value: 2, text: '2 - Ventas por efectuar' },
        { value: 3, text: '3 - Consignaciones' },
        { value: 4, text: '4 - Entrega gratuita' },
        { value: 5, text: '5 - Traslados internos' },
        { value: 6, text: '6 - Otros traslados no venta' },
        { value: 7, text: '7 - Guía de devolución' },
      ],
      despachos: [
        { value: 1, text: '1 - Despacho por cuenta del receptor' },
        { value: 2, text: '2 - Por cuenta del emisor a cliente' },
        { value: 3, text: '3 - Por cuenta del emisor a otras instalaciones' },
      ],
      previewHeaders: [
        { text: 'N° Local', value: 'code', width: 90 },
        { text: 'Nombre', value: 'name' },
        { text: 'Dirección', value: 'address' },
        { text: 'Comuna', value: 'comuna' },
        { text: 'Líneas', value: 'lines', align: 'end' },
        { text: 'Unidades', value: 'units', align: 'end' },
      ],
    }
  },
  computed: {
    canValidate() {
      return !!this.file && (!!this.batch || !!this.customer) && !this.loadingOt && !this.loadingCustomer
    },
    totals() {
      const t = (this.validation && this.validation.totals) || {}
      return Object.keys(t).map(name => ({ name: name.length > 50 ? name.slice(0, 50) + '…' : name, qty: t[name] }))
    },
    otherBatches() {
      return (this.validation && this.validation.existing_batches) || []
    },
  },
  watch: {
    value(open) {
      if (open) this.reset()
    },
    file(f) {
      if (f && !this.nameTouched && !this.batch) this.name = f.name.replace(/\.xlsx?$/i, '')
    },
  },
  methods: {
    reset() {
      this.validation = null
      this.file = null
      this.name = this.batch ? (this.batch.name || '') : ''
      this.nameTouched = false
      this.otNumber = ''
      this.otFound = false
      this.rut = ''
      this.customer = null
      this.rutNotFound = false
      this.params = emptyParams()
      if (this.batch) {
        this.params = Object.assign(emptyParams(), this.batch.params || {})
        this.params.transporte = Object.assign({}, (this.batch.params && this.batch.params.transporte) || {})
      } else if (this.initialOt) {
        this.otNumber = String(this.initialOt)
        this.fetchOt()
      }
    },
    close() {
      this.$emit('input', false)
    },
    searchOt() {
      if (this.otTimer) clearTimeout(this.otTimer)
      this.otTimer = setTimeout(this.fetchOt, 700)
    },
    async fetchOt() {
      this.otFound = false
      if (!this.otNumber) return
      this.loadingOt = true
      try {
        const data = await getWorkOrderforDTE(this.otNumber)
        this.otFound = true
        this.customer = data.customer ? { id: data.customer.id, name: data.customer.name, rut: data.customer.rut } : null
        this.rut = this.customer ? this.customer.rut : ''
        this.rutNotFound = false
        if (data.purchase_order && !this.params.oc_folio) this.params.oc_folio = String(data.purchase_order).trim()
      } catch (e) {
        notifications.showInfo('Orden de Trabajo no encontrada')
      } finally {
        this.loadingOt = false
      }
    },
    searchCustomer() {
      if (this.rutTimer) clearTimeout(this.rutTimer)
      this.rutTimer = setTimeout(async () => {
        this.customer = null
        this.rutNotFound = false
        if (!validaRUT(this.rut)) return
        this.loadingCustomer = true
        try {
          const data = await getCustomerByRUT(this.rut)
          this.customer = { id: data.id, name: data.name, rut: data.rut }
        } catch (e) {
          this.rutNotFound = true
        } finally {
          this.loadingCustomer = false
        }
      }, 700)
    },
    fields(dryRun) {
      const f = { file: this.file, params: this.params, name: (this.name || '').trim(), dry_run: dryRun ? 1 : '' }
      if (this.batch) f.batch_id = this.batch.id
      else {
        if (this.otFound) f.workorder_id = this.otNumber
        if (this.customer) f.customer_id = this.customer.id
      }
      return f
    },
    async validate() {
      this.working = true
      try {
        this.validation = await uploadDispatchDistribution(this.fields(true))
      } catch (e) {
        notifications.showError(typeof e === 'string' ? e : 'No se pudo validar el archivo')
      } finally {
        this.working = false
      }
    },
    async load() {
      this.working = true
      try {
        const batch = await uploadDispatchDistribution(this.fields(false))
        notifications.showSuccess('Pre-guías cargadas', batch.upload_result.created + ' nuevas, ' + batch.upload_result.updated + ' actualizadas')
        this.$emit('loaded', batch)
        this.close()
      } catch (e) {
        notifications.showError(typeof e === 'string' ? e : 'No se pudo cargar el archivo')
      } finally {
        this.working = false
      }
    },
  },
}
</script>
